import * as THREE from 'three';

/**
 * A much larger spaceship with a walkable interior.
 *
 * Layout (local X = forward):
 *   Front  (+X): Cockpit with windshield, pilot seat
 *   Middle:      Navigation console (holographic table)
 *   Rear   (-X): Cargo bay / airlock door
 *
 * Interior floor is at local Y = 0.  The hull surrounds it.
 * Entry point is at the rear-left side.
 */
export class ShipModel {
  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'spaceship';

    // Sub-groups
    this.exteriorGroup = new THREE.Group();
    this.exteriorGroup.name = 'ship-exterior';
    this.interiorGroup = new THREE.Group();
    this.interiorGroup.name = 'ship-interior';

    // Key positions in local space
    this.pilotSeatPos = new THREE.Vector3(5.5, 1.2, 0);
    this.consolePos = new THREE.Vector3(0, 1.0, 0);
    this.entryPos = new THREE.Vector3(-5, 0.5, 4);   // door / ramp
    this.interiorSpawnPos = new THREE.Vector3(-3, 0.5, 0); // where player appears inside

    // Interior walkable bounds in ship-local space.
    // Note: Y bounds are for the player's eye/camera position, not feet.
    this.interiorBoundsMin = new THREE.Vector3(-6.8, 1.8, -2.7);
    this.interiorBoundsMax = new THREE.Vector3(6.8, 3.25, 2.7);

    this._buildExterior();
    this._buildInterior();

    this.group.add(this.exteriorGroup);
    this.group.add(this.interiorGroup);
    this.interiorGroup.visible = false;
  }

  /* ─────────────────────────── Exterior ─────────────────────────── */

  _buildExterior() {
    const g = this.exteriorGroup;
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x8899aa, roughness: 0.3, metalness: 0.7 });
    const darkMat  = new THREE.MeshStandardMaterial({ color: 0x556677, roughness: 0.4, metalness: 0.6 });

    // ── Main hull ── (elongated box with beveled feel)
    const hullGeo = new THREE.BoxGeometry(14, 4, 6, 1, 1, 1);
    const hull = new THREE.Mesh(hullGeo, metalMat);
    hull.position.set(0, 1, 0);
    hull.castShadow = true;
    hull.name = 'hull';
    g.add(hull);

    // Nose cone
    const noseGeo = new THREE.ConeGeometry(3, 5, 4);
    noseGeo.rotateZ(-Math.PI / 2);
    const nose = new THREE.Mesh(noseGeo, metalMat);
    nose.position.set(9.5, 1, 0);
    nose.castShadow = true;
    g.add(nose);

    // Cockpit windshield
    const windshieldMat = new THREE.MeshStandardMaterial({
      color: 0x88ccff, roughness: 0.05, metalness: 0.2,
      transparent: true, opacity: 0.45, side: THREE.DoubleSide,
    });
    const windshieldGeo = new THREE.SphereGeometry(2.8, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.45);
    const windshield = new THREE.Mesh(windshieldGeo, windshieldMat);
    windshield.position.set(6, 2.5, 0);
    windshield.name = 'cockpit-glass';
    g.add(windshield);

    // ── Wings ──
    const wingGeo = new THREE.BoxGeometry(6, 0.3, 10);
    const wingL = new THREE.Mesh(wingGeo, darkMat);
    wingL.position.set(-1, 0.5, 7);
    wingL.castShadow = true;
    g.add(wingL);
    const wingR = wingL.clone();
    wingR.position.z = -7;
    g.add(wingR);

    // Wing tips (angled up)
    const tipGeo = new THREE.BoxGeometry(2, 2, 0.3);
    const tipL = new THREE.Mesh(tipGeo, darkMat);
    tipL.position.set(-1, 1.5, 12);
    g.add(tipL);
    const tipR = tipL.clone();
    tipR.position.z = -12;
    g.add(tipR);

    // ── Engines ──
    const engineGeo = new THREE.CylinderGeometry(0.8, 1.0, 3, 12);
    engineGeo.rotateZ(Math.PI / 2);
    const engineMat = new THREE.MeshStandardMaterial({ color: 0x445566, roughness: 0.5, metalness: 0.8 });

    [-2, 2].forEach(z => {
      const engine = new THREE.Mesh(engineGeo, engineMat);
      engine.position.set(-8, 1, z);
      engine.name = 'engine';
      g.add(engine);

      const glowGeo = new THREE.SphereGeometry(0.7, 8, 8);
      const glowMat = new THREE.MeshStandardMaterial({
        color: 0x44aaff, emissive: 0x2266ff, emissiveIntensity: 2,
        transparent: true, opacity: 0.8,
      });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.set(-9.5, 1, z);
      glow.name = 'engine-glow';
      g.add(glow);
    });

    // ── Landing gear ──
    const legGeo = new THREE.CylinderGeometry(0.12, 0.2, 2.5, 6);
    const legMat = new THREE.MeshStandardMaterial({ color: 0x556677, metalness: 0.6 });
    const footGeo = new THREE.SphereGeometry(0.35, 8, 6);
    [[-4, -1.2, 2.5], [-4, -1.2, -2.5], [5, -1.2, 2], [5, -1.2, -2]].forEach(([x, y, z]) => {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(x, y, z);
      leg.name = 'landing-gear';
      g.add(leg);
      const foot = new THREE.Mesh(footGeo, legMat);
      foot.position.set(x, y - 1.25, z);
      g.add(foot);
    });

    // ── Entry ramp indicator ──
    const rampGeo = new THREE.BoxGeometry(3, 0.15, 2);
    const rampMat = new THREE.MeshStandardMaterial({ color: 0x667788, emissive: 0x223344, emissiveIntensity: 0.3 });
    const ramp = new THREE.Mesh(rampGeo, rampMat);
    ramp.position.set(-5, -0.5, 4);
    ramp.rotation.z = 0.2;
    ramp.name = 'entry-ramp';
    g.add(ramp);
  }

  /* ─────────────────────────── Interior ─────────────────────────── */

  _buildInterior() {
    const g = this.interiorGroup;
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x1e1e2e, roughness: 0.9, metalness: 0.2 });
    const trimMat  = new THREE.MeshStandardMaterial({ color: 0x334455, roughness: 0.5, metalness: 0.6 });
    const accentMat = new THREE.MeshStandardMaterial({ color: 0x4466aa, emissive: 0x223366, emissiveIntensity: 0.3 });

    // ── Floor ──
    const floorGeo = new THREE.BoxGeometry(14, 0.2, 5.5);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.position.set(0, 0, 0);
    floor.name = 'interior-floor';
    g.add(floor);

    // ── Walls (left, right, back, ceiling) ──
    // Left wall
    const sideGeo = new THREE.BoxGeometry(14, 3.5, 0.2);
    const wallL = new THREE.Mesh(sideGeo, trimMat);
    wallL.position.set(0, 1.75, 2.85);
    g.add(wallL);
    const wallR = wallL.clone();
    wallR.position.z = -2.85;
    g.add(wallR);

    // Back wall
    const backGeo = new THREE.BoxGeometry(0.2, 3.5, 5.5);
    const wallBack = new THREE.Mesh(backGeo, trimMat);
    wallBack.position.set(-7, 1.75, 0);
    g.add(wallBack);

    // Ceiling
    const ceilGeo = new THREE.BoxGeometry(14, 0.2, 5.5);
    const ceiling = new THREE.Mesh(ceilGeo, trimMat);
    ceiling.position.set(0, 3.5, 0);
    g.add(ceiling);

    // Floor strip lights
    [-2, 2].forEach(z => {
      const stripGeo = new THREE.BoxGeometry(12, 0.03, 0.15);
      const stripMat = new THREE.MeshStandardMaterial({ color: 0x4488ff, emissive: 0x2244aa, emissiveIntensity: 1, transparent: true, opacity: 0.6 });
      const strip = new THREE.Mesh(stripGeo, stripMat);
      strip.position.set(0, 0.12, z);
      g.add(strip);
    });

    // ═══════════ COCKPIT AREA (front) ═══════════

    // Pilot seat
    // Seat base
    const seatBaseGeo = new THREE.BoxGeometry(1.2, 0.6, 1.2);
    const seatBase = new THREE.Mesh(seatBaseGeo, new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.8 }));
    seatBase.position.copy(this.pilotSeatPos).add(new THREE.Vector3(0, -0.5, 0));
    g.add(seatBase);

    // Seat back
    const seatBackGeo = new THREE.BoxGeometry(0.3, 1.5, 1.0);
    const seatBack = new THREE.Mesh(seatBackGeo, new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.8 }));
    seatBack.position.copy(this.pilotSeatPos).add(new THREE.Vector3(-0.6, 0.2, 0));
    seatBack.name = 'pilot-seat';
    g.add(seatBack);

    // Dashboard in front of pilot
    const dashGeo = new THREE.BoxGeometry(1.5, 0.8, 3.5);
    const dash = new THREE.Mesh(dashGeo, trimMat);
    dash.position.set(6.5, 0.8, 0);
    g.add(dash);

    // Cockpit screens (three angled panels)
    const screenMat = new THREE.MeshStandardMaterial({ color: 0x0a1428, emissive: 0x1155cc, emissiveIntensity: 0.6 });
    [-1, 0, 1].forEach((zOff, idx) => {
      const sGeo = new THREE.PlaneGeometry(1.0, 0.6);
      const screen = new THREE.Mesh(sGeo, screenMat);
      screen.position.set(6.4, 1.35, zOff * 1.1);
      screen.rotation.y = Math.PI + zOff * 0.3;
      screen.rotation.x = -0.25;
      screen.name = idx === 1 ? 'cockpit-main-screen' : 'cockpit-screen';
      g.add(screen);
    });

    // ═══════════ NAVIGATION CONSOLE (middle) ═══════════

    // Holographic table
    const tableGeo = new THREE.CylinderGeometry(1.4, 1.2, 0.9, 8);
    const tableMat = new THREE.MeshStandardMaterial({ color: 0x2a3040, roughness: 0.5, metalness: 0.5 });
    const table = new THREE.Mesh(tableGeo, tableMat);
    table.position.copy(this.consolePos).add(new THREE.Vector3(0, -0.1, 0));
    table.name = 'nav-console-base';
    g.add(table);

    // Holographic display surface (top of table — glowing)
    const holoGeo = new THREE.CylinderGeometry(1.2, 1.2, 0.05, 32);
    const holoMat = new THREE.MeshStandardMaterial({
      color: 0x112244, emissive: 0x1144aa, emissiveIntensity: 0.8,
      transparent: true, opacity: 0.7,
    });
    const holo = new THREE.Mesh(holoGeo, holoMat);
    holo.position.copy(this.consolePos).add(new THREE.Vector3(0, 0.4, 0));
    holo.name = 'console-screen';
    g.add(holo);

    // Holographic projection beam
    const beamGeo = new THREE.CylinderGeometry(0.05, 1.0, 2, 16, 1, true);
    const beamMat = new THREE.MeshStandardMaterial({
      color: 0x2266ff, emissive: 0x1144ff, emissiveIntensity: 0.4,
      transparent: true, opacity: 0.08, side: THREE.DoubleSide, depthWrite: false,
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.copy(this.consolePos).add(new THREE.Vector3(0, 1.6, 0));
    beam.name = 'holo-beam';
    g.add(beam);

    // ═══════════ REAR / CARGO ═══════════

    // Storage crates
    const crateMat = new THREE.MeshStandardMaterial({ color: 0x3a3a2a, roughness: 0.9 });
    [[-5, 0.5, -1.8], [-5, 0.5, 1.8], [-4.2, 0.5, -1.8]].forEach(([x, y, z]) => {
      const crate = new THREE.Mesh(new THREE.BoxGeometry(1.2, 1.0, 1.2), crateMat);
      crate.position.set(x, y, z);
      g.add(crate);
    });

    // ── Planet Radio / Broadcast Box ──
    // Sits on top of a crate in the rear — lets you hear the whole planet's song
    const radioGroup = new THREE.Group();
    radioGroup.name = 'radio-box';
    radioGroup.position.set(-4.2, 1.15, -1.8); // on top of rear crate

    const radioMat = new THREE.MeshStandardMaterial({
      color: 0x1a2a3a, roughness: 0.4, metalness: 0.6,
    });
    const radioBody = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.35, 0.4), radioMat);
    radioGroup.add(radioBody);

    // Speaker grille
    const grilleMat = new THREE.MeshStandardMaterial({
      color: 0x334455, roughness: 0.8, metalness: 0.3,
    });
    const grille = new THREE.Mesh(new THREE.PlaneGeometry(0.45, 0.22), grilleMat);
    grille.position.set(0, 0, 0.201);
    radioGroup.add(grille);

    // Glowing indicator light
    const indicatorMat = new THREE.MeshStandardMaterial({
      color: 0x44ff88, emissive: 0x22aa55, emissiveIntensity: 0.6,
    });
    const indicator = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 8), indicatorMat);
    indicator.position.set(0.2, 0.18, 0.15);
    indicator.name = 'radio-indicator';
    radioGroup.add(indicator);

    // Small antenna
    const antennaMat = new THREE.MeshStandardMaterial({ color: 0x888888, metalness: 0.8 });
    const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.015, 0.5, 6), antennaMat);
    antenna.position.set(-0.15, 0.35, 0);
    antenna.rotation.z = 0.3;
    radioGroup.add(antenna);

    g.add(radioGroup);

    // Door frame (exit)
    const doorFrameGeo = new THREE.BoxGeometry(0.2, 3, 2);
    const doorFrame = new THREE.Mesh(doorFrameGeo, accentMat);
    doorFrame.position.set(-6.5, 1.5, 2.8);
    doorFrame.name = 'ship-door';
    g.add(doorFrame);

    // ═══════════ LIGHTING ═══════════

    // Main overhead lights
    const mainLight = new THREE.PointLight(0x6688cc, 1.0, 20);
    mainLight.position.set(0, 3.2, 0);
    g.add(mainLight);

    // Cockpit warm light
    const cockpitLight = new THREE.PointLight(0x88aaff, 0.6, 10);
    cockpitLight.position.set(5, 2.5, 0);
    g.add(cockpitLight);

    // Console glow
    const consoleLight = new THREE.PointLight(0x2244ff, 0.8, 8);
    consoleLight.position.set(this.consolePos.x, this.consolePos.y + 2, this.consolePos.z);
    g.add(consoleLight);

    // Rear dim light
    const rearLight = new THREE.PointLight(0xaa6633, 0.3, 8);
    rearLight.position.set(-5, 2.5, 0);
    g.add(rearLight);
  }

  /* ─────────────────────────── API ─────────────────────────── */

  /**
   * Entry point in world space (near the ramp).
   */
  getEntryPoint() {
    const pos = this.entryPos.clone();
    this.group.updateMatrixWorld(true);
    pos.applyMatrix4(this.group.matrixWorld);
    return pos;
  }

  /**
   * Where the player spawns inside the ship (world space).
   */
  getInteriorSpawnWorld() {
    const pos = this.interiorSpawnPos.clone();
    this.group.updateMatrixWorld(true);
    pos.applyMatrix4(this.group.matrixWorld);
    return pos;
  }

  /**
   * Pilot seat position (world space).
   */
  getPilotSeatWorld() {
    const pos = this.pilotSeatPos.clone();
    this.group.updateMatrixWorld(true);
    pos.applyMatrix4(this.group.matrixWorld);
    return pos;
  }

  /**
   * Console position (world space).
   */
  getConsoleWorld() {
    const pos = this.consolePos.clone().add(new THREE.Vector3(0, 0.5, 0));
    this.group.updateMatrixWorld(true);
    pos.applyMatrix4(this.group.matrixWorld);
    return pos;
  }

  /**
   * Clamp a world-space camera/player position to the walkable ship interior.
   * Returns which local axes were clamped.
   */
  constrainInteriorPosition(worldPosition, padding = 0.2) {
    this.group.updateMatrixWorld(true);

    const local = worldPosition.clone();
    this.group.worldToLocal(local);

    const minX = this.interiorBoundsMin.x + padding;
    const maxX = this.interiorBoundsMax.x - padding;
    const minY = this.interiorBoundsMin.y;
    const maxY = this.interiorBoundsMax.y;
    const minZ = this.interiorBoundsMin.z + padding;
    const maxZ = this.interiorBoundsMax.z - padding;

    const clampedX = Math.min(maxX, Math.max(minX, local.x));
    const clampedY = Math.min(maxY, Math.max(minY, local.y));
    const clampedZ = Math.min(maxZ, Math.max(minZ, local.z));

    const hitX = Math.abs(clampedX - local.x) > 1e-6;
    const hitY = Math.abs(clampedY - local.y) > 1e-6;
    const hitZ = Math.abs(clampedZ - local.z) > 1e-6;

    if (hitX || hitY || hitZ) {
      local.set(clampedX, clampedY, clampedZ);
      this.group.localToWorld(local);
      worldPosition.copy(local);
    }

    return { hitX, hitY, hitZ };
  }

  setEngineGlow(intensity) {
    this.group.traverse(child => {
      if (child.name === 'engine-glow' && child.material) {
        child.material.emissiveIntensity = intensity;
        child.material.opacity = 0.3 + intensity * 0.5;
      }
    });
  }

  showInterior(show) {
    this.interiorGroup.visible = show;
    // When inside, hide the exterior hull so the camera isn't blocked
    this.exteriorGroup.visible = !show;
  }

  update() {
    // Subtle hover animation when landed
    // (disabled during interior — only exterior idle bob)
  }
}
